import { FocusEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, FieldPath } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';

import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import { SupportedAdditionalInfoDtoIn } from '@/models/in/SupportedAdditionalInfoDtoIn';
import FormStepper from '@/components/ui/FormStepper/FormStepper';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import SupportGoal from './steps/SupportGoal';
import RequiredDetails from './steps/RequiredDetails';
import PaymentSetup from './steps/PaymentSetup';
import FundPaymentSetup from './steps/FundPaymentSetup';
import UserComments from './steps/UserComments';
import { supportRequestFormSchema, defaultSupportRequestFormValues, SupportRequestFormData, } from './schemas/supportRequestFormSchema';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { SupportRequestDtoOut } from '@/models/out/SupportRequestDtoOut';
import { mapFormDataToDto } from './utils/mapFormDataToDto';
import { mapDraftToFormData } from './utils/mapDraftToFormData';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import { useDeleteSupportRequestDraft } from '@/pages/SupportRequests/Drafts/useSupportRequestDrafts';
import { offerTaskSatisfaction } from '@/helpers/surveyStorage';
import AllowedFor from '@/models/common/AllowedFor';
import { SelectedProjectData, SelectedSupplierData } from './ChooseSupplier';
import { scrollToFirstInvalidField } from '@/helpers/formValidationUi';
import { EmptyFileError, FileReadError, translateFileReadError } from '@/helpers/fileReaderHelper';
import { extractUserFacingServerMessage } from '@/helpers/serverErrorMessage';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import {
  shouldAbandonOnUnmount,
  shouldCaptureFundFormAbandoned,
  firstErrorField,
} from '@/helpers/fundFormAnalytics';
import {
  SUPPORT_REQUEST_UNKNOWN_FIELD,
  fieldNameFromFocusTarget,
  supportRequestTypeFromFlow,
} from '@/helpers/supportRequestAnalytics';

const formatCountdownClock = (totalSeconds: number): string => {
  const clamped = Math.max(0, totalSeconds);
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const parseDuplicateRetry = (data: unknown): { remainingSeconds: number } | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const rec = data as Record<string, unknown>;
  const remaining = rec.remainingSeconds ?? rec.RemainingSeconds;
  if (typeof remaining !== 'number' || !Number.isFinite(remaining) || remaining <= 0) {
    return null;
  }
  return { remainingSeconds: Math.ceil(remaining) };
};

interface NewSupportRequestFormProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  selectedSupported: ContactDtoIn | null;
  selectedSupplier?: SelectedSupplierData | null;
  /**
   * HAT-1105: the project a fund-expense request was filed against, chosen before the supplier. Null
   * for the supported flow, and for a fund without the projects feature - whose requests are unchanged.
   */
  selectedProject?: SelectedProjectData | null;
  /**
   * A request filled in on a client that could not send it - see DraftsStrip. The form opens
   * on the answers it already holds, leaving only the documents to add. Undefined is the
   * ordinary new request and behaves exactly as before.
   */
  draft?: SupportRequestDraftDtoIn | null;
}

const NewSupportRequestForm = ({ onSuccess, onError, onSubmittingChange, selectedSupported, selectedSupplier, selectedProject, draft }: NewSupportRequestFormProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const queryClient = useQueryClient();
  const deleteDraft = useDeleteSupportRequestDraft();
  const isFundExpenseFlow = !!selectedSupplier;
  const requestType = supportRequestTypeFromFlow(isFundExpenseFlow);
  const { captureEvent } = usePostHogIdentification();
  const lastFieldRef = useRef<string>(SUPPORT_REQUEST_UNKNOWN_FIELD);
  const submittedOkRef = useRef(false);
  const abandonedRef = useRef(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [retryUntilMs, setRetryUntilMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const methods = useForm<SupportRequestFormData>({
    resolver: zodResolver(supportRequestFormSchema) as any,
    defaultValues: defaultSupportRequestFormValues,
    mode: 'onChange',
  });

  const supportedAdditionalInfoUrl = currentFund?.id && selectedSupported?.id
    ? `funds/${currentFund.id}/supporteds/${selectedSupported.id}/additional-info`
    : '';

  const { data: supportedAdditionalInfo } =
    useGetAuthApi<SupportedAdditionalInfoDtoIn>(supportedAdditionalInfoUrl);

  const { mutateAsync: createSupportRequest, isPending: isMutationPending } = usePostAuthApi<void, SupportRequestDtoOut>(
    currentFund?.id ? `funds/${currentFund.id}/support-requests` : ''
  );
  const [isPreparingSubmit, setIsPreparingSubmit] = useState(false);
  const isSubmitting = isPreparingSubmit || isMutationPending;

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
    return () => onSubmittingChange?.(false);
  }, [isSubmitting, onSubmittingChange]);

  useEffect(() => {
    if (retryUntilMs == null) {
      return;
    }
    const tick = () => {
      const current = Date.now();
      if (current >= retryUntilMs) {
        setRetryUntilMs(null);
        setFormError(null);
        return;
      }
      setNowMs(current);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [retryUntilMs]);

  useEffect(() => {
    methods.reset({
      ...defaultSupportRequestFormValues,
      supported: selectedSupported ?? null,
      selectedSupplierId: selectedSupplier?.id ?? null,
      supplierName: selectedSupplier?.name ?? null,
      supplierTaxId: selectedSupplier?.taxId ?? null,
      projectId: selectedProject?.id ?? null,
      /*
       * ⚠️ Last, so a draft's own supported and supplier win over the props. Both carry the
       * same values when a draft opened this form, but only the draft has the rest of it.
       * mapDraftToFormData returns null for a missing or unreadable draft, and spreading
       * null is a no-op - so a bad draft opens an empty form rather than breaking it.
       */
      ...mapDraftToFormData(draft),
    });
  }, [selectedSupported, selectedSupplier, selectedProject, draft, methods]);

  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.SUPPORT_REQUEST_STARTED, { type: requestType });
    const startedAt = Date.now();
    const abandon = () => {
      if (!shouldCaptureFundFormAbandoned({ submittedOk: submittedOkRef.current, abandoned: abandonedRef.current })) return;
      abandonedRef.current = true;
      captureEvent(ANALYTICS_EVENTS.SUPPORT_REQUEST_ABANDONED, {
        last_field: lastFieldRef.current,
        type: requestType,
      });
    };
    const handlePageHide = () => abandon();
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      if (!shouldAbandonOnUnmount(startedAt, Date.now())) return;
      abandon();
    };
    // captureEvent is recreated each render; requestType is the only input that should restart the funnel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType]);

  const trackFocusField = useCallback((event: FocusEvent) => {
    const field = fieldNameFromFocusTarget(event.target);
    if (field) lastFieldRef.current = field;
  }, []);

  const formatErrors = (errors: any): string => {
    const errorList: string[] = [];

    const extractErrors = (obj: any) => {
      Object.keys(obj).forEach((key) => {
        if (obj[key]?.message) {
          errorList.push(obj[key].message);
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          extractErrors(obj[key]);
        }
      });
    };

    extractErrors(errors);
    return errorList.length > 0 ? errorList.join('\n') : t('pleaseCompleteAllRequiredFields');
  };

  const validateFields = async (fields: FieldPath<SupportRequestFormData>[]) => {
    setFormError(null);
    const isValid = await methods.trigger(fields);
    if (!isValid) {
      setFormError(formatErrors(methods.formState.errors));
      scrollToFirstInvalidField();
      const field = firstErrorField(methods.formState.errors) ?? fields[0];
      if (field) lastFieldRef.current = String(field);
    }
    return isValid;
  };

  const isRequiredDetailsStepNeeded = (goal: any): boolean => {
    const needsChildSelection = 
      goal?.allowedFor === AllowedFor.SupportedChildren || 
      goal?.allowedFor === AllowedFor.SupportedAndSupportedChildren;
    
    const needsDocuments = 
      goal?.requiredDocuments && 
      goal.requiredDocuments.length > 0;

    // Wedding goal always needs RequiredDetails step for wedding date
    const isWeddingGoal = goal?.name === 'Wedding';
    
    // Kollel goal always needs RequiredDetails step for kollel details
    const isKollelGoal = goal?.name === 'Kollel';
    
    return needsChildSelection || needsDocuments || isWeddingGoal || isKollelGoal;
  };

  const selectedGoalWithLimits = methods.watch('goalWithLimits');

  const stepDefinitions: StepDefinition[] = useMemo(() => {
    const steps: StepDefinition[] = [];
    const currentGoal = selectedGoalWithLimits?.goal;
    const isFundExpenseGoal = currentGoal?.allowedFor === AllowedFor.Fund;

    // Step 1: SupportGoal (always shown — for beneficiaries picks supported goals, for fund expenses picks fund expense goals)
    steps.push({
      content: <SupportGoal selectedSupported={selectedSupported} isFundExpenseFlow={isFundExpenseFlow} />,
      validate: () => validateFields(['goalWithLimits']),
    });

    // Step 2: RequiredDetails (for both supported and fund expense goals)
    const shouldShowRequiredDetails = !currentGoal || isRequiredDetailsStepNeeded(currentGoal);
    
    if (shouldShowRequiredDetails) {
      steps.push({
        content: (
          <RequiredDetails
            children={supportedAdditionalInfo?.children || []}
          />
        ),
        validate: () => validateFields(['childId', 'documents', 'weddingDate', 'documentDate', 'kollelName', 'studyTimes', 'kollelAddress', 'studyMaterial', 'roshKollelName']),
      });
    }

    // Step 3: Payment Setup (different component based on goal type)
    if (isFundExpenseGoal) {
      steps.push({
        content: <FundPaymentSetup lockedProjectName={selectedProject?.name} />,
        validate: () => validateFields([
          'amount',
          'supplierName',
          'supplierTaxId',
          'invoiceNumber',
          'bankCode',
          'bankAccount',
          'bankBranch',
          'payerName',
          'payerIsraeliId',
          'exceptionsCommitteeAcknowledged',
          'exceedsBalanceComment',
        ]),
      });
    } else {
      steps.push({
        content: <PaymentSetup paymentInfos={supportedAdditionalInfo?.paymentInfos || []} />,
        validate: () => validateFields(['multiRequestTotalCount', 'paymentStartTiming', 'transferDayOfMonth', 'paymentInfoId', 'amount', 'exceptionsCommitteeAcknowledged', 'exceedsBalanceComment', 'thirdPartyReason', 'thirdPartyReasonDetails', 'thirdPartyPayeeName', 'thirdPartyPayeeIsraeliId', 'thirdPartyRelationship', 'thirdPartyBankCode', 'thirdPartyBankBranch', 'thirdPartyBankAccount']),
      });
    }

    // Step 4: UserComments (always)
    steps.push({
      content: <UserComments />,
      validate: () => true,
    });

    return steps;
  }, [selectedGoalWithLimits, supportedAdditionalInfo, selectedSupported, isFundExpenseFlow, selectedProject, validateFields, isRequiredDetailsStepNeeded]);

  const submitForm = async (data: SupportRequestFormData) => {
    if (!currentFund?.id || isSubmitting || (retryUntilMs != null && Date.now() < retryUntilMs)) {
      if (!currentFund?.id) {
        setFormError(t('errorMissingData'));
      }
      return;
    }

    setIsPreparingSubmit(true);
    try {
      const apiData = await mapFormDataToDto(data);
      await createSupportRequest(apiData);
      queryClient.invalidateQueries({ predicate: (query) =>
        query.queryKey[0]?.toString().includes('eligible-amount') ?? false
      });
      queryClient.invalidateQueries({ predicate: (query) =>
        (query.queryKey[0]?.toString().includes('financial-data') &&
        query.queryKey[0]?.toString().includes('balance')) ?? false
      });
      /*
       * ⚠️ Only after the request was actually created. A draft discarded any earlier - on
       * open, or before this line - would take the coordinator's answers with it if the
       * submit then failed, and they filled this in somewhere they cannot easily retype.
       * Deleting never throws, so a draft that outlives its request costs one stale row and
       * not a lost submission.
       */
      if (draft) await deleteDraft(draft.id);

      submittedOkRef.current = true;
      captureEvent(ANALYTICS_EVENTS.SUPPORT_REQUEST_SUBMITTED, { type: requestType });
      onSuccess?.();
      methods.reset(defaultSupportRequestFormValues);
      offerTaskSatisfaction('supportRequest');
    } catch (error: unknown) {
      const axiosData = (error as { response?: { data?: unknown } })?.response?.data;
      const duplicateRetry = parseDuplicateRetry(axiosData);
      if (duplicateRetry) {
        const until = Date.now() + duplicateRetry.remainingSeconds * 1000;
        setRetryUntilMs(until);
        setNowMs(Date.now());
        const display = t('duplicateSupportRequestTitle');
        setFormError(display);
        onError?.(display);
        return;
      }
      setRetryUntilMs(null);
      // HAT-1109: הודעת שרת מוצגת רק אם היא נוסחה למשתמש (כלומר בעברית). שומרי הסף
      // הפנימיים של ה-API מחזירים אנגלית, וזה מה שקפץ לרכזת הקרן במסך התשלום.
      const display =
        extractUserFacingServerMessage(axiosData)
        ?? (error instanceof EmptyFileError || error instanceof FileReadError
          ? translateFileReadError(error, t)
          : t('errorCreatingSupportRequest'));
      setFormError(display);
      onError?.(display);
    } finally {
      setIsPreparingSubmit(false);
    }
  };

  const handleSubmit = () => {
    return methods.handleSubmit(
      submitForm,
      (errors) => {
        setRetryUntilMs(null);
        setFormError(formatErrors(errors));
        scrollToFirstInvalidField();
      }
    )();
  };

  const remainingSeconds = retryUntilMs == null ? 0 : Math.max(0, Math.ceil((retryUntilMs - nowMs) / 1000));
  const countdownLabel = formatCountdownClock(remainingSeconds);
  const remainingMinutes = Math.ceil(remainingSeconds / 60);
  const remainingMinutesText = remainingMinutes > 1
    ? t('duplicateSupportRequestMinutesLeft', { count: remainingMinutes })
    : remainingMinutes === 1
      ? t('duplicateSupportRequestOneMinute')
      : t('duplicateSupportRequestLessThanMinute');
  const errorMessage: ReactNode = retryUntilMs != null ? (
    <span className="flex flex-col items-start gap-1">
      <span>{t('duplicateSupportRequestTitle')} {remainingMinutesText}</span>
      <span
        role="timer"
        dir="ltr"
        className="tabular-nums heading-h3-semibold tracking-wide"
        aria-label={countdownLabel}
      >
        {countdownLabel}
      </span>
      <span>{t('duplicateSupportRequestRemainingSuffix')}</span>
    </span>
  ) : formError;

  return (
    <FormProvider {...methods}>
      <div onFocus={trackFocusField}>
      <FormStepper
        steps={stepDefinitions}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting || methods.formState.isSubmitting}
        submitDisabled={remainingSeconds > 0}
        errorMessage={errorMessage}
      />
      </div>
    </FormProvider>
  );
};

export default NewSupportRequestForm;

